export type ParsedAddress = {
	address1: string;
	city: string;
	state: string;
	postalCode: string;
	country: string;
};

// Splits a "ST zip" segment (e.g. "OH 43210") into its parts, returning empty
// strings when the segment isn't a two-letter state followed by a code.
const parseStateZip = (
	segment: string,
): { state: string; postalCode: string } => {
	const trimmed = segment.trim();
	const spaceIndex = trimmed.indexOf(" ");
	if (spaceIndex === -1) {
		return { state: "", postalCode: "" };
	}
	const state = trimmed.slice(0, spaceIndex);
	if (!/^[A-Za-z]{2}$/.test(state)) {
		return { state: "", postalCode: "" };
	}
	return { state, postalCode: trimmed.slice(spaceIndex + 1).trim() };
};

/**
 * The shipment stores the stop address as a single formatted string
 * ("address1, City, ST zip, Country"). Split it back into its parts so the edit
 * form can be prefilled. Best-effort: unparseable segments fall back to empty.
 */
export const parseCombinedAddress = (value?: string | null): ParsedAddress => {
	const empty: ParsedAddress = {
		address1: "",
		city: "",
		state: "",
		postalCode: "",
		country: "",
	};
	if (!value) {
		return empty;
	}
	const segments = value
		.split(",")
		.map((part) => part.trim())
		.filter((part) => part.length > 0);
	if (segments.length === 0) {
		return empty;
	}

	const working = [...segments];

	// A trailing segment that isn't a "ST zip" pair is the country.
	let country = "";
	if (parseStateZip(working.at(-1) ?? "").state === "") {
		country = working.pop() ?? "";
	}

	const { state, postalCode } = parseStateZip(working.pop() ?? "");
	const city = working.pop() ?? "";
	const address1 = working.join(", ");

	return { address1, city, state, postalCode, country };
};
